import React from 'react';
import { Body7, Container } from '@ui';
import { FlatButton, SkeletonButton } from '@ui/buttons';
import { Consequence, MicroLabel, ReadOnlyJson, Row } from '@features/scoreSpec/styles';
import { deriveSchema } from '@features/scoreSpec/schemaFromPaths';

// What a prose spec is offered instead of a shape editor: the shape its own paths already describe.
//
// This is not a migration of the source. The source keeps its `format_prompt`, keeps loading and keeps
// running — untouched. What gets a declared shape is the COPY, which is a new spec, and giving a new
// spec an enforced output contract is what this route exists to do. Without it the shape editor is
// unreachable for the 26 of 29 shipped specs written in prose, and the only way to author a schema is
// to hand-write one in the advanced editor's JSON pane, next to the paths that already describe it.
//
// Derived, not guessed: `output_extraction` names every node the answer must contain, so every path
// resolves against the result by construction (schemaFromPaths.js).
//
// Two presses, not one. The shape is shown before it is taken, because the leaf TYPES are assumed — a
// number and a string are indistinguishable from a path — and because a prose contract can ask for
// fields nothing extracts, which a schema will forbid the model from sending at all.

export const DeclareSchemaPanel = ({ contract }) => {
  const [isShowing, setIsShowing] = React.useState(false);

  const output = React.useMemo(
    () => ({
      scores: (contract.model.scores || []).map((score) => ({
        ...score.raw,
        name: score.name,
        jsonpath: score.jsonpath,
      })),
      extra_columns: (contract.model.extraColumns || []).reduce((map, column) => {
        if (column.name) map[column.name] = column.jsonpath;
        return map;
      }, {}),
    }),
    [contract.model]
  );

  const derived = React.useMemo(() => deriveSchema(output), [output]);

  if (!derived.schema) {
    return (
      <div>
        <Consequence $tone="warn">{derived.blocked}</Consequence>
        <Container top={8}>
          <Body7 isLowContrast>
            So this copy carries the prose across as it is. The advanced editor's JSON pane is where a shape gets typed
            out.
          </Body7>
        </Container>
      </div>
    );
  }

  if (!isShowing) {
    return (
      <div>
        <Body7 isLowContrast>
          The paths this spec reads already describe the shape of the answer, so the copy can declare it instead of
          asking for it in prose. The source is not changed either way.
        </Body7>
        <Container top={8}>
          <SkeletonButton onClick={() => setIsShowing(true)}>Declare an output_schema from these paths</SkeletonButton>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <MicroLabel>The shape these paths describe</MicroLabel>
      <Container top={8}>
        <ReadOnlyJson>{JSON.stringify(derived.schema, null, 2)}</ReadOnlyJson>
      </Container>
      <Container top={8}>
        <Consequence>
          Every path this spec reads resolves against it. The leaf types are assumed — they become editable rows once
          taken.
        </Consequence>
      </Container>

      {derived.unresolvable.length > 0 && (
        <Container top={8}>
          <Consequence $tone="warn">
            {`${derived.unresolvable.map((u) => u.label).join(', ')} could not be read, so nothing was declared for ${
              derived.unresolvable.length === 1 ? 'it' : 'them'
            }. Add the missing node in the JSON editor afterwards.`}
          </Consequence>
        </Container>
      )}

      <Container top={8}>
        <Consequence $tone="warn">
          A schema closes every object, so the model may no longer answer with a field the shape does not declare. If
          the prose above asks for one that nothing extracts — a thinking field it reasons in, say — add it after taking
          this shape, or its absence can move the scores.
        </Consequence>
      </Container>

      <Container top={16}>
        <Row>
          <FlatButton onClick={() => contract.adoptSchema(derived.schema)}>Use this shape</FlatButton>
          <SkeletonButton onClick={() => setIsShowing(false)}>Keep the prose</SkeletonButton>
        </Row>
      </Container>
    </div>
  );
};
